import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import { BillingBanner } from './billing/BillingPage.js';
import type { MessageKey } from '@mrigl/i18n';
import { LOCALES, pickLocalized, type Locale } from '@mrigl/shared';
import { Alert, Button, Card, CheckboxGroup, SelectField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useState, type SubmitEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import { useI18n } from '../i18n.js';
import { useSession } from '../session.js';
import { StoreOverview } from './analytics/AnalyticsPage.js';

type StoreDetails = Schemas['StoreDetails'];
type SlugCheck = Schemas['SlugCheck'];
type Member = Schemas['MemberList']['items'][number];
type Invitation = Schemas['InvitationSummary'];

const SLUG_CHECK_DELAY_MS = 300;
const ASSIGNABLE_ROLES = ['admin', 'staff', 'support'] as const;

function languageOptions(t: ReturnType<typeof useI18n>['t']) {
  return LOCALES.map((value) => ({ value, label: t(`admin.language.${value}`), lang: value }));
}

/** Store creation with a live address availability check. */
export function NewStorePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [check, setCheck] = useState<SlugCheck | null>(null);
  const [defaultLocale, setDefaultLocale] = useState<Locale>(locale);
  const [enabledLocales, setEnabledLocales] = useState<string[]>([locale]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const candidate = slug.trim().toLowerCase();
    if (candidate.length === 0) {
      setCheck(null);
      return;
    }
    const timer = setTimeout(() => {
      void unwrap(
        api.GET('/admin-api/v1/store-slugs/{slug}', { params: { path: { slug: candidate } } }),
      )
        .then(setCheck)
        .catch(() => {
          setCheck(null);
        });
    }, SLUG_CHECK_DELAY_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [slug]);

  const slugError =
    check === null || check.available || check.reason === undefined
      ? undefined
      : t(`admin.store.slug.${check.reason}` as MessageKey);
  const localesValid = enabledLocales.includes(defaultLocale);

  async function create(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const store = await unwrap(
        api.POST('/admin-api/v1/stores', {
          body: {
            slug: slug.trim().toLowerCase(),
            name: { [defaultLocale]: name.trim() },
            defaultLocale,
            enabledLocales: enabledLocales as Locale[],
          },
        }),
      );
      await refresh();
      // New stores are offline until published: the onboarding wizard comes next (ADR 0026).
      void navigate(`/stores/${store.storeId}/setup`, { replace: true });
    } catch (failure) {
      setError(
        failure instanceof ApiError && failure.code === 'conflict'
          ? t('admin.store.slug.taken')
          : errorMessage(t, failure),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mrigl-stack">
      <h1 className="mrigl-heading">{t('admin.store.new.title')}</h1>
      <p className="mrigl-muted">{t('admin.store.new.intro')}</p>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <form className="mrigl-card mrigl-stack" onSubmit={(event) => void create(event)}>
        <TextField
          label={t('admin.store.nameIn', { language: t(`admin.language.${defaultLocale}`) })}
          name="name"
          required
          maxLength={120}
          lang={defaultLocale}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextField
          label={t('admin.store.slug')}
          hint={
            check?.available === true
              ? t('admin.store.slugAvailable', { host: '⁦' + check.host + '⁩' })
              : t('admin.store.slugHint')
          }
          {...(slugError === undefined ? {} : { error: slugError })}
          name="slug"
          required
          ltr
          autoComplete="off"
          spellCheck={false}
          maxLength={40}
          value={slug}
          onChange={(event) => {
            setSlug(event.target.value.toLowerCase());
          }}
        />
        <CheckboxGroup
          legend={t('admin.store.enabledLocales')}
          options={languageOptions(t)}
          values={enabledLocales}
          onChange={setEnabledLocales}
        />
        <SelectField
          label={t('admin.store.defaultLocale')}
          value={defaultLocale}
          onChange={(event) => {
            setDefaultLocale(event.target.value as Locale);
          }}
          options={languageOptions(t)}
        />
        {localesValid ? null : <Alert tone="error">{t('admin.store.error.defaultLocale')}</Alert>}
        <Button type="submit" busy={busy} disabled={check?.available !== true || !localesValid}>
          {t('admin.store.create')}
        </Button>
      </form>
    </div>
  );
}

/** Store page: addresses, settings and team (sections depend on the member's permissions). */
export function StorePage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const [store, setStore] = useState<StoreDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStore(
        await unwrap(api.GET('/admin-api/v1/stores/{storeId}', { params: { path: { storeId } } })),
      );
    } catch (failure) {
      setError(errorMessage(t, failure));
    }
  }, [storeId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null) return <Alert tone="error">{error}</Alert>;
  if (store === null) return <Loading />;
  const can = (permission: string) => store.permissions.includes(permission);

  return (
    <div className="mrigl-stack">
      {can('billing.read') ? <BillingBanner storeId={store.storeId} /> : null}
      {store.publishedAt === null ? (
        <p className="admin-notice" role="status">
          {t('admin.setup.banner')}{' '}
          <Link to={`/stores/${store.storeId}/setup`}>{t('admin.setup.finish')}</Link>
        </p>
      ) : null}
      <h1 className="mrigl-heading">
        {pickLocalized(store.name, locale, store.defaultLocale) ?? store.slug}
      </h1>
      {can('analytics.read') ? <StoreOverview storeId={store.storeId} /> : null}
      <Card labelledBy="addresses-title">
        <h2 id="addresses-title" className="mrigl-subheading">
          {t('admin.store.addresses')}
        </h2>
        <ul className="mrigl-list">
          {store.hosts.map((host) => (
            <li key={host.host}>
              <a
                className="mrigl-ltr"
                href={`https://${host.host}`}
                rel="noreferrer"
                target="_blank"
              >
                {host.host}
              </a>
            </li>
          ))}
        </ul>
      </Card>
      {can('store.update') ? <StoreSettings store={store} onSaved={setStore} /> : null}
      {can('staff.read') ? (
        <TeamSection storeId={store.storeId} canManage={can('staff.manage')} />
      ) : null}
    </div>
  );
}

function StoreSettings({
  store,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly onSaved: (store: StoreDetails) => void;
}) {
  const { t } = useI18n();
  const [names, setNames] = useState<Record<string, string>>(store.name);
  const [enabledLocales, setEnabledLocales] = useState<string[]>(store.enabledLocales);
  const [defaultLocale, setDefaultLocale] = useState<Locale>(store.defaultLocale);
  const [status, setStatus] = useState<
    { tone: 'success' } | { tone: 'error'; error: unknown } | null
  >(null);
  const [busy, setBusy] = useState(false);
  const valid =
    enabledLocales.includes(defaultLocale) && (names[defaultLocale] ?? '').trim().length > 0;

  async function save(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const name = Object.fromEntries(
        Object.entries(names).filter(
          ([key, value]) => enabledLocales.includes(key) && value.trim().length > 0,
        ),
      );
      const updated = await unwrap(
        api.PATCH('/admin-api/v1/stores/{storeId}', {
          params: { path: { storeId: store.storeId } },
          body: { name, defaultLocale, enabledLocales: enabledLocales as Locale[] },
        }),
      );
      onSaved(updated);
      setStatus({ tone: 'success' });
    } catch (failure) {
      setStatus({ tone: 'error', error: failure });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-labelledby="settings-title"
      onSubmit={(event) => void save(event)}
    >
      <h2 id="settings-title" className="mrigl-subheading">
        {t('admin.store.settings')}
      </h2>
      {status === null ? null : (
        <Alert tone={status.tone}>
          {status.tone === 'success' ? t('admin.saved') : errorMessage(t, status.error)}
        </Alert>
      )}
      <CheckboxGroup
        legend={t('admin.store.enabledLocales')}
        options={languageOptions(t)}
        values={enabledLocales}
        onChange={setEnabledLocales}
      />
      {LOCALES.filter((value) => enabledLocales.includes(value)).map((value) => (
        <TextField
          key={value}
          label={t('admin.store.nameIn', { language: t(`admin.language.${value}`) })}
          lang={value}
          maxLength={120}
          value={names[value] ?? ''}
          onChange={(event) => {
            setNames({ ...names, [value]: event.target.value });
          }}
        />
      ))}
      <SelectField
        label={t('admin.store.defaultLocale')}
        value={defaultLocale}
        onChange={(event) => {
          setDefaultLocale(event.target.value as Locale);
        }}
        options={languageOptions(t).filter((option) => enabledLocales.includes(option.value))}
      />
      {valid ? null : <Alert tone="error">{t('admin.store.error.defaultLocale')}</Alert>}
      <Button type="submit" busy={busy} disabled={!valid}>
        {t('admin.save')}
      </Button>
    </form>
  );
}

function TeamSection({
  storeId,
  canManage,
}: {
  readonly storeId: string;
  readonly canManage: boolean;
}) {
  const { t, formatDateTime, locale } = useI18n();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof ASSIGNABLE_ROLES)[number]>('staff');
  const [inviteLocale, setInviteLocale] = useState<Locale>(locale);
  const [status, setStatus] = useState<
    { tone: 'success' } | { tone: 'error'; error: unknown } | null
  >(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const path = { params: { path: { storeId } } };
    const [memberList, invitationList] = await Promise.all([
      unwrap(api.GET('/admin-api/v1/stores/{storeId}/members', path)),
      unwrap(api.GET('/admin-api/v1/stores/{storeId}/invitations', path)),
    ]);
    setMembers(memberList.items);
    setInvitations(invitationList.items);
  }, [storeId]);

  useEffect(() => {
    void load().catch((failure: unknown) => {
      setStatus({ tone: 'error', error: failure });
    });
  }, [load]);

  async function run(action: () => Promise<void>, success = false) {
    setStatus(null);
    try {
      await action();
      if (success) setStatus({ tone: 'success' });
      await load();
    } catch (failure) {
      setStatus({ tone: 'error', error: failure });
    }
  }

  const roleLabel = (key: string) => t(`admin.role.${key}` as MessageKey);
  const displayName = (member: Member) => member.displayName ?? member.email ?? member.userId;

  return (
    <Card labelledBy="team-title">
      <h2 id="team-title" className="mrigl-subheading">
        {t('admin.store.team')}
      </h2>
      {status === null ? null : (
        <Alert tone={status.tone}>
          {status.tone === 'success' ? t('admin.team.sent') : errorMessage(t, status.error)}
        </Alert>
      )}
      <h3 className="mrigl-subheading">{t('admin.team.members')}</h3>
      {members === null ? (
        <Loading />
      ) : (
        <ul className="mrigl-list">
          {members.map((member) => {
            const editable = canManage && !member.you && member.role !== 'owner';
            return (
              <li key={member.membershipId} className="mrigl-row">
                <strong>{displayName(member)}</strong>
                {member.displayName === null || member.email === null ? null : (
                  <span className="mrigl-muted mrigl-ltr">{member.email}</span>
                )}
                {member.you ? <span className="mrigl-muted">{t('admin.team.you')}</span> : null}
                {editable ? (
                  <>
                    <SelectField
                      label={t('admin.team.roleFor', { name: displayName(member) })}
                      value={member.role}
                      onChange={(event) =>
                        void run(async () => {
                          await unwrap(
                            api.PATCH('/admin-api/v1/stores/{storeId}/members/{membershipId}', {
                              params: { path: { storeId, membershipId: member.membershipId } },
                              body: {
                                role: event.target.value as (typeof ASSIGNABLE_ROLES)[number],
                              },
                            }),
                          );
                        })
                      }
                      options={ASSIGNABLE_ROLES.map((value) => ({
                        value,
                        label: roleLabel(value),
                      }))}
                    />
                    <Button
                      variant="secondary"
                      aria-label={t('admin.team.removeFor', { name: displayName(member) })}
                      onClick={() =>
                        void run(async () => {
                          await unwrap(
                            api.PATCH('/admin-api/v1/stores/{storeId}/members/{membershipId}', {
                              params: { path: { storeId, membershipId: member.membershipId } },
                              body: { status: 'revoked' },
                            }),
                          );
                        })
                      }
                    >
                      {t('admin.team.remove')}
                    </Button>
                  </>
                ) : (
                  <span className="mrigl-muted">{roleLabel(member.role)}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <h3 className="mrigl-subheading">{t('admin.team.invitations')}</h3>
      {invitations.length === 0 ? (
        <p className="mrigl-muted">{t('admin.team.noInvitations')}</p>
      ) : (
        <ul className="mrigl-list">
          {invitations.map((invitation) => (
            <li key={invitation.invitationId} className="mrigl-row">
              <span className="mrigl-ltr">{invitation.emailHint}</span>
              <span className="mrigl-muted">{roleLabel(invitation.role)}</span>
              <span className="mrigl-muted">
                {t('admin.team.expires', { date: formatDateTime(invitation.expiresAt) })}
              </span>
              {canManage ? (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void run(async () => {
                      await unwrap(
                        api.DELETE('/admin-api/v1/stores/{storeId}/invitations/{invitationId}', {
                          params: { path: { storeId, invitationId: invitation.invitationId } },
                        }),
                      );
                    })
                  }
                >
                  {t('admin.team.cancelInvitation')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canManage ? (
        <form
          className="mrigl-stack"
          aria-labelledby="invite-title"
          onSubmit={(event: SubmitEvent) => {
            event.preventDefault();
            setBusy(true);
            void run(async () => {
              await unwrap(
                api.POST('/admin-api/v1/stores/{storeId}/invitations', {
                  params: { path: { storeId } },
                  body: { email: email.trim(), role, locale: inviteLocale },
                }),
              );
              setEmail('');
            }, true).finally(() => {
              setBusy(false);
            });
          }}
        >
          <h3 id="invite-title" className="mrigl-subheading">
            {t('admin.team.invite')}
          </h3>
          <TextField
            label={t('admin.signIn.email')}
            type="email"
            name="inviteEmail"
            required
            ltr
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <SelectField
            label={t('admin.team.role')}
            value={role}
            onChange={(event) => {
              setRole(event.target.value as (typeof ASSIGNABLE_ROLES)[number]);
            }}
            options={ASSIGNABLE_ROLES.map((value) => ({ value, label: roleLabel(value) }))}
          />
          <SelectField
            label={t('admin.team.inviteLocale')}
            value={inviteLocale}
            onChange={(event) => {
              setInviteLocale(event.target.value as Locale);
            }}
            options={languageOptions(t)}
          />
          <Button type="submit" busy={busy}>
            {t('admin.team.send')}
          </Button>
        </form>
      ) : null}
    </Card>
  );
}
